import { zodResolver } from "@hookform/resolvers/zod";
import UserValidator from "Validators/Common/User";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import * as z from "zod";

import AdminRoutes from "Routes/Components/Admin";

import { HTTPErrorCallback } from "Helpers/Error";
import { inputMasks } from "Helpers/Format";
import { clearMask } from "Helpers/Util";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import FormWrapper from "Components/Admin/Form/FormWrapper";
import Input from "Components/Admin/Form/Input";
import Switch from "Components/Admin/Form/Switch";
import Section from "Components/Admin/Section";
import ToggleStatus from "Components/Admin/ToggleStatus";

import * as S from "./styles";

const UpdateUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();

  const [selectedUser, setSelectedUser] = useState({});

  useEffect(() => {
    if (id) {
      return getUser(id);
    }

    // eslint-disable-next-line
  }, [id]);

  const formSchema = z.object({
    full_name: UserValidator.full_name,
    email: z.string().email({ message: "E-mail inválido" }),
    document: UserValidator.document,
    responsible_name: UserValidator.responsible_name,
    phone: UserValidator.phone,
    opt_in: z.boolean().optional(),
  });

  const methods = useForm({
    resolver: zodResolver(formSchema),
  });
  const {
    register,
    handleSubmit,
    control,
    formState: { isDirty },
    setValue,
  } = methods;

  const getUser = (id) => {
    api
      .post("/users/get-user-by-id", { user_id: id })
      .then((response) => {
        const user = response.data;
        setSelectedUser(user);

        setValue("full_name", user.full_name);
        setValue("email", user.email);
        // ⚠️ `|| ""` NÃO é cosmético nos campos MASCARADOS: o react-input-mask
        // faz `value.split("")` no componentDidUpdate e estoura
        // "Cannot read properties of null" com valor nulo — a tela inteira fica
        // BRANCA, sem mensagem. `document` era o único sem o fallback aqui, e
        // 34 dos 125 usuários da listagem do admin têm document nulo.
        setValue("document", user.document || "");
        setValue("responsible_name", user.responsible_name || "");
        setValue("phone", user.phone || "");
        setValue("opt_in", user.opt_in ?? false);
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  const resendOptInEmail = () => {
    setLoading(true);

    api
      .post("/users/resend-opt-in-email", { user_id: selectedUser.id })
      .then(() => {
        setLoading(false);
        showNotification(
          "success",
          "Sucesso!",
          "E-mail de validação reenviado com sucesso!",
        );
      })
      .catch((error) => {
        setLoading(false);
        return HTTPErrorCallback(error);
      });
  };

  const submit = (data) => {
    setLoading(true);

    data.document = clearMask(data.document);
    if (data.phone) data.phone = clearMask(data.phone);

    api
      .post("/users/update-user", {
        user_id: selectedUser.id,
        user: data,
      })
      .then(() => {
        setLoading(false);

        showNotification(
          "success",
          "Sucesso!",
          "Usuário atualizado com sucesso!",
        );
      })
      .catch((error) => {
        setLoading(false);
        return HTTPErrorCallback(error);
      });
  };

  return (
    <Section>
      <S.Wrapper>
        <S.Buttons>
          <Button
            type="button"
            text="Voltar"
            disabled={loading}
            onClick={() => navigate(AdminRoutes.ListUsers.path)}
            width="30%"
          />
          <ToggleStatus
            route={"/users/toggle-user-status/"}
            item={selectedUser}
            refresh={() => getUser(id)}
          />
        </S.Buttons>
        <FormWrapper submit={handleSubmit(submit)} {...methods}>
          <Input
            {...register("full_name")}
            label="Instituição"
            placeholder="Nome da instituição"
            type="text"
            required
          />
          <Input
            {...register("document")}
            label="CNPJ"
            placeholder="CNPJ"
            type="text"
            mask={inputMasks.document}
            required
          />
          <Input
            {...register("responsible_name")}
            label="Contato"
            placeholder="Nome do responsável"
            type="text"
            required
          />
          <Input
            {...register("phone")}
            label="Telefone"
            placeholder="Telefone"
            type="text"
            mask={inputMasks.phone}
            required
          />
          <Input
            {...register("email")}
            type="email"
            label="E-mail"
            placeholder="E-mail"
            required
          />

          <S.OptInWrapper>
            <S.OptInLabel>
              E-mail validado?
              <S.TooltipIcon title="O e-mail precisa estar validado para permitir o login no sistema">
                ?
              </S.TooltipIcon>
            </S.OptInLabel>
            <Controller
              name="opt_in"
              control={control}
              render={({ field }) => (
                <Switch
                  name="opt_in"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
            <Button
              type="button"
              text="Reenviar e-mail de validação"
              onClick={resendOptInEmail}
              disabled={loading}
            />
          </S.OptInWrapper>

          {selectedUser?.document_src ? (
            <S.Link
              href={selectedUser.document_src}
              target="_blank"
              rel="noreferrer"
            >
              <span>Visualizar cartão de CPF ou CNPJ</span>
            </S.Link>
          ) : null}

          <Button
            type="submit"
            text="Atualizar"
            disabled={loading || !isDirty}
          />
        </FormWrapper>
      </S.Wrapper>
    </Section>
  );
};

export default UpdateUser;
